import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Flex, Spinner, Text, useToast, VStack,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { BountyCategory } from '@foodfluence/shared';
import { InfluencerDataContext } from '../../../contexts/InfluencerDataContext';
import { useBountyData } from '../../../contexts/BountyDataContext';
import { bountyApi } from '../../../services/api/bounties';
import BountyBackButton from '../components/BountyBackButton';
import ProposalFormFields from '../components/ProposalFormFields';
import {
  formValuesToSubmitParams, ProposalFieldKey, ProposalFormValues, proposalToFormValues, validateProposalForm,
} from '../proposalFormUtils';
import { myBountiesPath } from '../utils';
import StickyBottomCta from '../components/StickyBottomCta';
import { ff } from '../../../theme';
import { DisplayHeading } from '../../../components/ui';
import { bountyColors, eyebrowStyle, pageBottomPaddingWithStickyCta } from '../theme';

const CATEGORY_LABELS: Record<BountyCategory, string> = {
  cpgBrand: 'CPG Brands',
  restaurant: 'Restaurants',
  hotelsLodging: 'Hotels & lodging',
  other: 'Other',
};

const SummaryRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Flex gap="12px" py="10px" borderTop="1px solid" borderColor={ff.line}>
    <Text w="120px" flexShrink={0} fontSize="13px" color={ff.text2} lineHeight="1.4">{label}</Text>
    <Text flex={1} minW={0} fontSize="14px" fontWeight={600} lineHeight="1.4" wordBreak="break-word" color={ff.ink}>{value}</Text>
  </Flex>
);

const ResubmitProposalPage: React.FC = () => {
  const { proposalId } = useParams<{ proposalId: string }>();
  const { influencerData } = useContext(InfluencerDataContext);
  const { proposals, loading, refetchBounties } = useBountyData();
  const navigate = useNavigate();
  const toast = useToast();

  const proposal = useMemo(
    () => proposals.find(p => p.id === proposalId && p.status === 'rejected'),
    [proposals, proposalId],
  );

  const [values, setValues] = useState<ProposalFormValues>({
    companyName: '', website: '', instagramHandle: '', category: '', proposalNote: '',
  });
  const [errors, setErrors] = useState<Partial<Record<ProposalFieldKey, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!proposal) return;
    setValues(proposalToFormValues(proposal));
  }, [proposal]);

  useEffect(() => {
    if (!loading && proposalId && !proposal) {
      navigate(myBountiesPath, { replace: true });
    }
  }, [loading, proposal, proposalId, navigate]);

  const handleChange = (key: ProposalFieldKey, value: string) => {
    setValues(prev => ({ ...prev, [key]: value }));
    setErrors(prev => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const handleSubmit = async () => {
    if (!influencerData?.id || !proposal) return;
    const found = validateProposalForm(values);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setIsSubmitting(true);
    try {
      await bountyApi.updateProposal(influencerData.id, proposal.id, formValuesToSubmitParams(values));
      await refetchBounties();
      toast({
        title: 'Bounty resubmitted',
        description: 'We\'ll review your updated submission and get back to you.',
        status: 'success', duration: 5000, isClosable: true,
      });
      navigate(myBountiesPath, { replace: true });
    } catch (error) {
      toast({
        title: 'Could not resubmit bounty',
        description: error instanceof Error ? error.message : 'Please try again.',
        status: 'error', duration: 5000, isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || !proposal) {
    return (
      <Flex minH="100svh" align="center" justify="center" bg="white">
        <Spinner color={ff.muted} />
      </Flex>
    );
  }

  const originalRows = [
    { label: 'Company', value: proposal.companyName },
    { label: 'Website', value: proposal.website ?? '—' },
    ...(proposal.instagramHandle
      ? [{ label: 'Instagram', value: `@${proposal.instagramHandle.replace(/^@+/, '')}` }]
      : []),
    { label: 'Category', value: CATEGORY_LABELS[proposal.category] },
    { label: 'Your connection', value: proposal.proposalNote ?? '—' },
  ];

  return (
    <Box minH="100svh" bg="white" fontFamily="display" color={ff.ink} lineHeight="normal" px="20px" pt="12px" pb={pageBottomPaddingWithStickyCta} overflowX="hidden">
      <Box ml="-8px">
        <BountyBackButton to={myBountiesPath} replace aria-label="Back to My Bounties" />
      </Box>
      <VStack spacing="18px" align="stretch" pt="8px">
        <Box>
          <DisplayHeading size="34px">Resubmit {proposal.companyName}</DisplayHeading>
          <Text fontSize="15px" lineHeight="1.4" color="#55555A" mt="8px">
            Your original submission is below. Update anything that&apos;s changed, then send it back for review.
          </Text>
        </Box>

        {proposal.rejectionNote && (
          <Box px="14px" py="12px" borderRadius="14px" bg={bountyColors.redTint} color={bountyColors.redInk} fontSize="14px" lineHeight="1.4">
            <Text as="strong">Why it wasn&apos;t approved:</Text> {proposal.rejectionNote}
          </Box>
        )}

        <Box border="1px solid" borderColor="#E4E4E0" borderRadius="18px" px="16px" pt="16px" pb="4px">
          <Text as="h2" {...eyebrowStyle} pb="10px">What you submitted</Text>
          {originalRows.map(row => (
            <SummaryRow key={row.label} label={row.label} value={row.value} />
          ))}
        </Box>

        <Box>
          <Text as="h2" {...eyebrowStyle}>Update your submission</Text>
          <Text fontSize="14px" color={ff.text2} mt="4px" mb="16px">
            Fields are prefilled from your last submission — change only what needs fixing.
          </Text>
          <ProposalFormFields values={values} errors={errors} onChange={handleChange} />
        </Box>
      </VStack>

      <StickyBottomCta>
        <Button
          variant="lime"
          w="full"
          h="56px"
          borderRadius="full"
          fontSize="16px"
          isLoading={isSubmitting}
          onClick={handleSubmit}
        >
          Resubmit for Review
        </Button>
      </StickyBottomCta>
    </Box>
  );
};

export default ResubmitProposalPage;
